import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Subject,
  takeUntil,
} from 'rxjs';
import { DemandReason } from 'server/shared/models/demandReason';
import { queryParam$ } from '../../../helpers/queryParam$';
import { CustomerService } from '../../services/customer.service';

@Component({
  selector: 'app-demand-reason-select',
  templateUrl: './demand-reason-select.component.html',
  styleUrls: ['./demand-reason-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DemandReasonSelectComponent),
      multi: true,
    },
  ],
})
export class DemandReasonSelectComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  private destroyed$ = new Subject<void>();
  private suppressParamSync = false;

  @Input() useQueryParam?: string;
  @Input() label?: string;
  @Input() disabled = false;
  @Input() required = false;
  @Input() lightFormField = false;
  @Input() showDescription = false;
  @Input() descriptionControl?: FormControl;

  @Output() selectionChanged = new EventEmitter<DemandReason | null>();

  selectedDemandReason$ = new BehaviorSubject<DemandReason | null>(null);

  demandReasons$ = this.customerService.readyDispoCustomer$.pipe(
    map((customer) => customer?.demandReasons || []),
  );

  // ControlValueAccessor implementation
  private onChange: (value: DemandReason | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private customerService: CustomerService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (this.useQueryParam) {
      queryParam$(this.route, this.useQueryParam)
        .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
        .subscribe((param) => {
          if (!this.suppressParamSync) {
            if (param && param !== 'ALL') {
              // Find the demand reason by ID
              this.demandReasons$
                .pipe(takeUntil(this.destroyed$))
                .subscribe((demandReasons) => {
                  const demandReason = demandReasons.find(
                    (dr) => dr.id === param,
                  );
                  if (demandReason) {
                    this.selectedDemandReason$.next(demandReason);
                    this.onChange(demandReason);
                  }
                });
            } else {
              this.selectedDemandReason$.next(null);
              this.onChange(null);
            }
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onDemandReasonChange(demandReason: DemandReason | null): void {
    this.suppressParamSync = true;
    this.selectedDemandReason$.next(demandReason);
    this.onChange(demandReason);
    this.onTouched();
    this.selectionChanged.emit(demandReason);

    if (this.useQueryParam) {
      const paramValue = demandReason?.id || null;
      this.updateQueryParam(paramValue);
    }

    setTimeout(() => {
      this.suppressParamSync = false;
    }, 0);
  }

  private updateQueryParam(paramValue: string | null): void {
    const queryParams: any = {};
    queryParams[this.useQueryParam || ''] = paramValue;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  // ControlValueAccessor methods
  writeValue(value: DemandReason | null): void {
    this.selectedDemandReason$.next(value);
  }

  registerOnChange(fn: (value: DemandReason | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
